import {getBaseFetchHeaders} from '@github-ui/fetch-headers'
import {useMutation, useQueries, useQuery, useQueryClient} from '@github-ui/react-query'
// eslint-disable-next-line no-restricted-imports -- The JS clamp here intentionally mirrors the CSS media-query breakpoint; deriving from `ScreenSize.large` keeps the two in lockstep.
import {ScreenSize} from '@github-ui/screen-size'
import {verifiedFetch} from '@github-ui/verified-fetch'
import type {RefObject} from 'react'
import {useCallback, useEffect, useEffectEvent, useRef, useState} from 'react'

import {copilotLocalStorage} from './copilot-local-storage'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from './CopilotChatContext'
import {useChatManager} from './CopilotChatManagerContext'

export interface IndexingState {
  code: TopicIndexStatus
  docs: TopicIndexStatus
  requestStatus: CanIndexStatus
  remainingRepoIndexTokens?: number
}

interface IndexingStateResponse {
  code_status: TopicIndexStatus
  docs_status: TopicIndexStatus
  can_index: CanIndexStatus
}

export const TopicIndexStatus = {
  Unindexed: 'not_indexed',
  Indexed: 'indexed',
  Indexing: 'indexing',
  PartiallyIndexed: 'partially_indexed',
  Unknown: 'unknown',
} as const

export type TopicIndexStatus = (typeof TopicIndexStatus)[keyof typeof TopicIndexStatus]

export const CanIndexStatus = {
  Requested: 'requested',
  NotFound: 'not_found',
  Unauthorized: 'unauthorized',
  ServiceUnavailable: 'service_unavailable',
  QuotaExhausted: 'quota_exhausted',
  Forbidden: 'forbidden',
  CanIndex: 'ok',
  IndexingError: 'indexing_error',
  RequestFailed: 'request_failed',
  Unknown: 'unknown',
} as const

export type CanIndexStatus = (typeof CanIndexStatus)[keyof typeof CanIndexStatus]

const errorStatuses = [
  CanIndexStatus.NotFound,
  CanIndexStatus.Unauthorized,
  CanIndexStatus.ServiceUnavailable,
  CanIndexStatus.QuotaExhausted,
  CanIndexStatus.Forbidden,
  CanIndexStatus.RequestFailed,
]

const UNKNOWN_INDEXING_STATE: IndexingState = {
  requestStatus: CanIndexStatus.Unknown,
  code: TopicIndexStatus.Unknown,
  docs: TopicIndexStatus.Unknown,
}

export const MIN_PANEL_HEIGHT = 256
export const MIN_PANEL_WIDTH = 400

// Breakpoint at which `ChatPanel.module.css` switches to the mobile
// fullscreen layout (`max-height: 100dvh`, `max-width: 100dvw`). The JS
// clamps mirror the active CSS cap so React state stays in sync with
// what's rendered (see `getPanelHeight` / `getPanelWidth`). Derived
// from the shared `ScreenSize.large` (768) token so this stays aligned
// with the rest of the codebase's responsive breakpoints — the CSS
// media query uses `max-width: 767px`, which matches
// `window.innerWidth < ScreenSize.large`. Hoisted to module scope so
// `exhaustive-deps` recognises it as a stable constant.
const MOBILE_BREAKPOINT_PX = ScreenSize.large - 1

export function useReposIndexingState(nwos: string[]): [IndexingState, () => void] {
  const client = useQueryClient()

  const invalidateIndexingStatus = useCallback(
    () =>
      Promise.all(nwos.map(nwo => client.invalidateQueries({queryKey: ['copilot-chat', 'repos-indexing-state', nwo]}))),
    [client, nwos],
  )

  const results = useQueries({
    queries: nwos.map(nwo => ({
      queryKey: ['copilot-chat', 'repos-indexing-state', nwo],
      queryFn: () => fetchIndexingStatus(nwo),
      placeholderData: UNKNOWN_INDEXING_STATE,
      staleTime: Infinity,
    })),
  })

  const {mutate: triggerIndexingForRepos} = useMutation({
    mutationKey: ['repos-indexing-state'],
    mutationFn: () => triggerIndexing(nwos),
    onSuccess: invalidateIndexingStatus,
  })

  const aggregatedIndexingStatus = aggregateResults(results.map(r => r.data ?? UNKNOWN_INDEXING_STATE))

  // If any repo is "indexing" and we have not encountered an error, we consider indexing to be in progress
  const isIndexingInProgress =
    aggregatedIndexingStatus.code === TopicIndexStatus.Indexing &&
    aggregatedIndexingStatus.requestStatus !== CanIndexStatus.IndexingError

  useInterval(() => void invalidateIndexingStatus(), isIndexingInProgress ? 10000 : 0)

  return [aggregatedIndexingStatus, () => triggerIndexingForRepos()]
}

export function useRepoIndexingState(nwo: string): [IndexingState, () => void] {
  const client = useQueryClient()

  const invalidateIndexingStatus = useCallback(
    () => Promise.resolve(client.invalidateQueries({queryKey: ['repo-indexing-state', nwo]})),
    [client, nwo],
  )

  const result = useQuery({
    queryKey: ['copilot-chat', 'repo-indexing-state', nwo],
    queryFn: () => fetchIndexingStatus(nwo),
    placeholderData: UNKNOWN_INDEXING_STATE,
    staleTime: Infinity,
  })

  const {mutate: triggerIndexingForRepos} = useMutation({
    mutationKey: ['repo-indexing-state'],
    mutationFn: () => triggerIndexing([nwo]),
    onSuccess: invalidateIndexingStatus,
  })

  const indexingStatus = result.data ?? UNKNOWN_INDEXING_STATE

  // If repo is "indexing" and we have not encountered an error, we consider indexing to be in progress
  const isIndexingInProgress =
    indexingStatus.code === TopicIndexStatus.Indexing && indexingStatus.requestStatus !== CanIndexStatus.IndexingError

  useInterval(() => void invalidateIndexingStatus(), isIndexingInProgress ? 10000 : 0)

  return [indexingStatus, () => triggerIndexingForRepos()]
}

export function useShowTopicPicker() {
  const state = useChatState()
  const manager = useChatManager()
  const showTopicPicker =
    (!state.selectedThreadID || state.mode === 'assistive') && state.messages.length === 0 && !state.currentTopic

  useEffect(() => {
    // When the thread updates, see if we need to show a blank thread.
    manager.showTopicPicker(showTopicPicker)
  }, [manager, showTopicPicker])
}

export type ResizeEdgeHorizontal = false | 'left' | 'right'
export type ResizeEdgeVertical = false | 'top' | 'bottom'

export interface ResizePositionHandlers {
  getRight: () => number
  getBottom: () => number
  setRight: (v: number) => void
  setBottom: (v: number) => void
}

export interface UseResizablePanelOptions {
  maxPanelWidth?: number
  positionHandlersRef?: RefObject<ResizePositionHandlers | null>
  /**
   * Whether the resizable panel is currently open/visible. Required so
   * every host opts into the gated behavior:
   *
   *  - When `false`, the hook skips the dynamic-default height read
   *    (which calls `document.querySelector('.AppHeader').getBoundingClientRect()`
   *    and forces a synchronous layout) and skips the viewport refresh
   *    listeners. This avoids layout thrash on page loads where chat is
   *    never opened.
   *  - When `true`, the hook re-evaluates the dynamic default and clamps
   *    state to the live viewport on resize.
   *
   * Adding a new caller that doesn't have a concrete open/closed signal?
   * Pass `true` (forces the measuring path) rather than omitting — the
   * type is required precisely so that decision is explicit.
   */
  isOpen: boolean
}

export function useResizablePanel(options: UseResizablePanelOptions) {
  const {maxPanelWidth, positionHandlersRef, isOpen} = options
  // The dynamic default panel height depends on the bottom offset at which
  // the panel is actually rendered. Only the draggable embedded host
  // renders at the persisted `panelPositionBottom`; the standard assistive
  // `ChatPanel` is CSS-anchored at `DEFAULT_PANEL_POSITION_BOTTOM`. We
  // detect the draggable host via `positionHandlersRef` so the persisted
  // bottom only feeds the default-height formula in that case (otherwise
  // dragging the embedded panel up would shrink the standard panel's
  // default height by the same amount on the next mount).
  const isDraggable = positionHandlersRef !== undefined
  const readStoredPanelHeight = useCallback(
    () => copilotLocalStorage.getPanelHeight(isDraggable ? copilotLocalStorage.getPanelPositionBottom() : undefined),
    [isDraggable],
  )
  const storedPanelWidth = copilotLocalStorage.getPanelWidth()
  // Clamp the initial width against maxPanelWidth so a previously stored
  // width wider than the cap does not render at full width on first paint.
  // Viewport-based clamping happens in the effect below once remSizeRef is populated.
  const initialPanelWidth =
    maxPanelWidth !== undefined
      ? Math.min(Math.max(storedPanelWidth, MIN_PANEL_WIDTH), maxPanelWidth)
      : storedPanelWidth
  // Seed the initial panel height. The host component (e.g.
  // ChatPanelWithHeader) mounts on every page load even when the user
  // never opens Copilot, and the dynamic-default formula calls
  // `document.querySelector('.AppHeader').getBoundingClientRect()`, which
  // forces a synchronous style/layout pass. When the host tells us the
  // panel is closed we defer that read to the refresh effect below, which
  // runs once `isOpen` flips true.
  const [panelHeight, setPanelHeight] = useState(() =>
    isOpen ? readStoredPanelHeight() : copilotLocalStorage.getInitialPanelHeight(),
  )
  const [panelWidth, setPanelWidth] = useState(initialPanelWidth)
  const resizeStartYRef = useRef<number | null>(null)
  const resizeStartHeightRef = useRef(panelHeight)
  const newHeightRef = useRef(panelHeight)
  const resizeStartXRef = useRef<number | null>(null)
  const resizeStartWidthRef = useRef(initialPanelWidth)
  const newWidthRef = useRef(initialPanelWidth)
  const remSizeRef = useRef(0)

  // Track resize direction and start position for right/bottom edge resizing
  const invertXRef = useRef(false)
  const invertYRef = useRef(false)
  const resizeStartPositionRightRef = useRef(0)
  const resizeStartPositionBottomRef = useRef(0)
  const newPositionRightRef = useRef(0)
  const newPositionBottomRef = useRef(0)

  useEffect(() => {
    remSizeRef.current = parseFloat(getComputedStyle(document.documentElement).fontSize)
  }, [])

  // Breakpoint at which `ChatPanel.module.css` switches to the mobile
  // fullscreen layout is defined as the module-level constant
  // `MOBILE_BREAKPOINT_PX` above.

  const getPanelHeight = (height: number) => {
    // On narrow viewports (≤767px) the panel uses the mobile fullscreen
    // layout: `height: 100%` with `max-height: 100dvh` (see
    // ChatPanel.module.css). The rendered height is the viewport,
    // independent of `--panel-height`, so React state must equal the
    // viewport in both directions — not just clamp from above. A smaller
    // stored/default value passing through unchanged would leave state
    // below the rendered height and reintroduce the drag-staleness gap
    // this clamp exists to prevent.
    if (window.innerWidth <= MOBILE_BREAKPOINT_PX) {
      return window.innerHeight
    }
    // Desktop: upper bound must match the active CSS `max-height` on
    // `.copilotChatPanel`: `calc(100vh - 1rem - 60px)`. If the JS clamp
    // is looser than the CSS cap, state can sit above the rendered
    // height — and when the user then drags the bottom edge up to
    // shrink, they have to drag through the entire gap before anything
    // visibly moves (state changes but CSS keeps clipping to its
    // tighter cap).
    const upperBound = window.innerHeight - remSizeRef.current - 60
    return Math.min(Math.max(height, MIN_PANEL_HEIGHT), upperBound)
  }

  const getPanelWidth = useCallback(
    (width: number) => {
      // On narrow viewports (≤767px) the panel uses the mobile fullscreen
      // layout: `width: 100%` with `max-width: 100dvw`. The rendered
      // width is the viewport, independent of `--panel-width`, so React
      // state must equal the viewport in both directions — see
      // `getPanelHeight` for the symmetric reasoning. `maxPanelWidth` is
      // intentionally ignored here because the CSS fullscreen rules
      // override it.
      if (window.innerWidth <= MOBILE_BREAKPOINT_PX) {
        return window.innerWidth
      }
      // Desktop: mirror the active CSS `max-width` of `calc(100vw - 2rem)`.
      const cssCap = window.innerWidth - 2 * remSizeRef.current
      const upperBound = Math.min(cssCap, maxPanelWidth ?? Number.POSITIVE_INFINITY)
      return Math.min(Math.max(width, MIN_PANEL_WIDTH), upperBound)
    },
    [maxPanelWidth],
  )

  // When the panel transitions from closed -> open, swap the static
  // `getInitialPanelHeight()` fallback for the dynamic-default / stored
  // height *during render*, before the panel becomes visible. Without
  // this the first open render commits at the 600px fallback and the
  // height refresh effect only corrects it on the following render, which
  // visibly jumps the panel (e.g. from 600 to ~912 on a 1000px viewport
  // when no height is stored). Setting state during render cancels the
  // current render and restarts with the corrected value before any paint.
  // We track previous `isOpen` via state (React's canonical "derive on
  // change" pattern) so the swap fires exactly once per closed->open
  // transition. `remSizeRef` is populated by the mount effect above,
  // which runs strictly before any user-driven open.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen)
    if (isOpen) {
      setPanelHeight(prev => {
        const next = getPanelHeight(readStoredPanelHeight())
        if (prev === next) return prev
        // Keep `newHeightRef` aligned with the committed height. `stopResize`
        // persists `newHeightRef.current` for vertical resizes; if a user
        // starts and ends a resize on the same tick (no mousemove) before
        // this ref is updated, the stale initial-seed height would otherwise
        // overwrite the freshly-computed default in storage.
        newHeightRef.current = next
        return next
      })
      // Mirror the same refresh for width. The mount/viewport effects below
      // re-clamp the stored width post-paint, but if the panel mounted while
      // closed on a narrow viewport (width state pinned to that clamp) and
      // the user then widened the window before opening, the first open
      // paint would otherwise show the stale narrow width and only jump up
      // to the stored preference on the following render. Refreshing here
      // during the same closed->open render commits the correct width
      // before the panel becomes visible.
      setPanelWidth(prev => {
        const next = getPanelWidth(copilotLocalStorage.getPanelWidth())
        if (prev === next) return prev
        newWidthRef.current = next
        return next
      })
    }
  }

  // After mount, re-clamp the stored width against the full viewport/max
  // constraints. We only sync React state here — storage is left untouched
  // so the user's saved preference survives short-viewport mounts and is
  // restored when the viewport grows back. The refresh effect below
  // preserves storage in the same shrink scenario; persisting here would
  // be inconsistent with that and destroy the saved value on any page
  // load with chat already open on a narrow viewport.
  useEffect(() => {
    const clamped = getPanelWidth(storedPanelWidth)
    if (clamped !== storedPanelWidth) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Clamping depends on post-mount viewport/rem measurements that aren't available during render.
      setPanelWidth(clamped)
      newWidthRef.current = clamped
    }
    // Run only on mount / when the max constraint changes
  }, [getPanelWidth, storedPanelWidth])

  // Keep panel width in sync with the viewport while the panel is open.
  // Mirrors the height refresh effect below: `ChatPanel.module.css` caps
  // the rendered width at `calc(100vw - 2rem)`, so a stored width larger
  // than the current viewport leaves React state stale relative to what
  // is visible. Without this, dragging the resize handle has to "consume"
  // the stale delta before the panel visibly changes width. We re-clamp
  // state (downward only for stored widths, so the user's preference
  // survives a return to a wider viewport) without persisting.
  useEffect(() => {
    if (!isOpen) return

    const refresh = () => {
      if (resizeStartXRef.current !== null) return
      // `next` is the stored width clamped to the current viewport/max
      // bound. It is `<= stored` by construction, so assigning it directly
      // gives the right behaviour in both directions:
      //  - viewport shrinks below stored: state follows `next` down
      //  - viewport grows back: `next` returns to the stored value and
      //    state follows it up to the user's saved preference
      // (Using `Math.min(prev, next)` here would pin state at the smaller
      // clamped value forever once shrunk.)
      const next = getPanelWidth(copilotLocalStorage.getPanelWidth())
      setPanelWidth(prev => {
        if (prev === next) return prev
        newWidthRef.current = next
        return next
      })
    }

    refresh()

    // Listen to the window `resize` event rather than observing
    // `document.documentElement`: on scrollable pages the document
    // element's box is content-sized, so a ResizeObserver doesn't fire
    // when only the viewport shrinks. `window.resize` is the reliable
    // signal here.
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', refresh)
    return () => window.removeEventListener('resize', refresh)
  }, [isOpen, getPanelWidth])

  // After mount, re-clamp the initial height against the viewport. The
  // initial state is seeded with the raw stored value (or dynamic default)
  // because `getPanelHeight` depends on post-mount `remSizeRef`/viewport
  // measurements. On short viewports a previously stored height can exceed
  // the resize-time cap; clamp here so state stays in sync with what the
  // resize handlers will allow.
  //
  // We only sync React state — storage is left untouched so the user's
  // saved preference survives short-viewport mounts and is restored when
  // the viewport grows back. The refresh effect below preserves storage
  // in the same shrink scenario; persisting here would be inconsistent
  // with that and destroy the saved value on any page load with chat
  // already open on a short viewport.
  useEffect(() => {
    // `panelHeight` here is still the initial render value: this effect runs
    // once on mount, before any setPanelHeight call has had a chance to fire.
    const clamped = getPanelHeight(panelHeight)
    if (clamped !== panelHeight) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Clamping depends on post-mount viewport/rem measurements that aren't available during render.
      setPanelHeight(clamped)
      newHeightRef.current = clamped
    }
    // Run only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep panel height in sync with the viewport. Two related problems:
  //
  //   1. Users WITHOUT a stored height should track the dynamic default
  //      (which depends on the page header position) as the viewport
  //      changes — already handled by recomputing from
  //      `getPanelHeight(copilotLocalStorage.getPanelHeight())`.
  //
  //   2. Users WITH a stored height that exceeds the current viewport
  //      need their displayed height clamped, otherwise React state stays
  //      e.g. 800px while the panel CSS `max-height` visually caps it at
  //      ~484px. The drag handlers seed `resizeStartHeightRef` from React
  //      state, so a stale 800 means the user must drag ~316px before
  //      anything visibly changes. We re-clamp state on viewport changes
  //      so the displayed and stored values stay aligned with what drag
  //      can actually apply. Crucially, we do NOT persist the clamp here:
  //      the user's original (larger) preference must survive a return
  //      to a taller viewport.
  //
  // We skip while the panel is closed (state isn't user-visible) and
  // while a drag-resize is in progress (don't clobber an active gesture).
  useEffect(() => {
    if (!isOpen) return

    const refresh = () => {
      if (resizeStartYRef.current !== null) return
      // `next` is the stored height clamped to the current viewport. It is
      // `<= stored` by construction, so assigning it directly gives the
      // right behaviour in both directions:
      //  - viewport shrinks below stored: state follows `next` down
      //  - viewport grows back: `next` returns to the stored value and
      //    state follows it up to the user's saved preference
      // (Using `Math.min(prev, next)` here would pin state at the smaller
      // clamped value forever once shrunk.)
      const next = getPanelHeight(readStoredPanelHeight())
      setPanelHeight(prev => {
        if (prev === next) return prev
        newHeightRef.current = next
        return next
      })
    }

    refresh()

    // Listen to the window `resize` event rather than observing
    // `document.documentElement`: on scrollable pages the document
    // element's box is content-sized, so a ResizeObserver doesn't fire
    // when only the viewport shrinks. `window.resize` is the reliable
    // signal here.
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', refresh)
    return () => window.removeEventListener('resize', refresh)
  }, [isOpen, readStoredPanelHeight])

  const resize = useCallback(
    (e: MouseEvent) => {
      if (resizeStartYRef.current !== null) {
        const dy = invertYRef.current ? e.clientY - resizeStartYRef.current : resizeStartYRef.current - e.clientY
        let height = getPanelHeight(resizeStartHeightRef.current + dy)

        // Clamp so the panel doesn't extend past the viewport edge
        if (positionHandlersRef?.current) {
          if (invertYRef.current) {
            // Bottom edge resize: position can decrease to 0 at most
            height = Math.min(height, resizeStartHeightRef.current + resizeStartPositionBottomRef.current)
          } else {
            // Top edge resize: position stays fixed
            height = Math.min(height, document.documentElement.clientHeight - positionHandlersRef.current.getBottom())
          }
        }

        setPanelHeight(height)
        newHeightRef.current = height

        if (invertYRef.current && positionHandlersRef?.current) {
          const heightDelta = height - resizeStartHeightRef.current
          const newBottom = Math.max(0, resizeStartPositionBottomRef.current - heightDelta)
          positionHandlersRef.current.setBottom(newBottom)
          newPositionBottomRef.current = newBottom
        }
      }
      if (resizeStartXRef.current !== null) {
        const dx = invertXRef.current ? e.clientX - resizeStartXRef.current : resizeStartXRef.current - e.clientX
        let width = getPanelWidth(resizeStartWidthRef.current + dx)

        // Clamp so the panel doesn't extend past the viewport edge
        if (positionHandlersRef?.current) {
          if (invertXRef.current) {
            // Right edge resize: position can decrease to 0 at most
            width = Math.min(width, resizeStartWidthRef.current + resizeStartPositionRightRef.current)
          } else {
            // Left edge resize: position stays fixed
            width = Math.min(width, document.documentElement.clientWidth - positionHandlersRef.current.getRight())
          }
        }

        setPanelWidth(width)
        newWidthRef.current = width

        if (invertXRef.current && positionHandlersRef?.current) {
          const widthDelta = width - resizeStartWidthRef.current
          const newRight = Math.max(0, resizeStartPositionRightRef.current - widthDelta)
          positionHandlersRef.current.setRight(newRight)
          newPositionRightRef.current = newRight
        }
      }
    },
    [getPanelWidth, positionHandlersRef],
  )

  const stopResize = useCallback(() => {
    window.removeEventListener('mousemove', resize)
    // eslint-disable-next-line react-hooks/immutability
    window.removeEventListener('mouseup', stopResize)
    if (resizeStartYRef.current !== null) {
      // use newHeight ref since panelHeight might not be updated yet.
      copilotLocalStorage.setPanelHeight(newHeightRef.current)
      resizeStartYRef.current = null
    }
    if (resizeStartXRef.current !== null) {
      copilotLocalStorage.setPanelWidth(newWidthRef.current)
      resizeStartXRef.current = null
    }
    // Persist adjusted position for right/bottom edge resizes only if the
    // position was actually updated during the resize (requires positionHandlersRef).
    if (invertYRef.current && positionHandlersRef?.current) {
      copilotLocalStorage.setPanelPositionBottom(newPositionBottomRef.current)
    }
    if (invertXRef.current && positionHandlersRef?.current) {
      copilotLocalStorage.setPanelPositionRight(newPositionRightRef.current)
    }
    invertXRef.current = false
    invertYRef.current = false
  }, [resize, positionHandlersRef])

  const startResize = useCallback(
    (e: React.MouseEvent, horizontal: ResizeEdgeHorizontal, vertical: ResizeEdgeVertical) => {
      if (e.button === 0) {
        e.preventDefault()
        if (vertical) {
          resizeStartYRef.current = e.clientY
          resizeStartHeightRef.current = panelHeight
          invertYRef.current = vertical === 'bottom'
          if (invertYRef.current && positionHandlersRef?.current) {
            resizeStartPositionBottomRef.current = positionHandlersRef.current.getBottom()
          }
        }
        if (horizontal) {
          resizeStartXRef.current = e.clientX
          resizeStartWidthRef.current = panelWidth
          invertXRef.current = horizontal === 'right'
          if (invertXRef.current && positionHandlersRef?.current) {
            resizeStartPositionRightRef.current = positionHandlersRef.current.getRight()
          }
        }
        window.addEventListener('mousemove', resize)
        window.addEventListener('mouseup', stopResize)
      }
    },
    [panelHeight, panelWidth, resize, stopResize, positionHandlersRef],
  )

  const onResizerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'ArrowUp') {
        const newPanelHeight = getPanelHeight(panelHeight + 4)
        setPanelHeight(newPanelHeight)
        copilotLocalStorage.setPanelHeight(newPanelHeight)
        e.preventDefault()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'ArrowDown') {
        const newPanelHeight = getPanelHeight(panelHeight - 4)
        setPanelHeight(newPanelHeight)
        copilotLocalStorage.setPanelHeight(newPanelHeight)
        e.preventDefault()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'ArrowRight') {
        const newPanelWidth = getPanelWidth(panelWidth - 4)
        setPanelWidth(newPanelWidth)
        copilotLocalStorage.setPanelWidth(newPanelWidth)
        e.preventDefault()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'ArrowLeft') {
        const newPanelWidth = getPanelWidth(panelWidth + 4)
        setPanelWidth(newPanelWidth)
        copilotLocalStorage.setPanelWidth(newPanelWidth)
        e.preventDefault()
      }
    },
    [getPanelWidth, panelHeight, panelWidth],
  )

  return {panelWidth, panelHeight, startResize, onResizerKeyDown}
}

export function useDraggablePanel(panelWidth: number, panelHeight: number) {
  const [positionRight, setPositionRight] = useState(() => copilotLocalStorage.getPanelPositionRight())
  const [positionBottom, setPositionBottom] = useState(() => copilotLocalStorage.getPanelPositionBottom())
  const [isDragging, setIsDragging] = useState(false)

  const dragStartRef = useRef<{mouseX: number; mouseY: number; startRight: number; startBottom: number} | null>(null)
  const newPositionRef = useRef({right: positionRight, bottom: positionBottom})

  const onDragMove = useCallback(
    (e: MouseEvent) => {
      const start = dragStartRef.current
      if (!start) return

      const dx = start.mouseX - e.clientX
      const dy = start.mouseY - e.clientY

      const newRight = Math.max(0, Math.min(start.startRight + dx, document.documentElement.clientWidth - panelWidth))
      const newBottom = Math.max(
        0,
        Math.min(start.startBottom + dy, document.documentElement.clientHeight - panelHeight),
      )

      setPositionRight(newRight)
      setPositionBottom(newBottom)
      newPositionRef.current = {right: newRight, bottom: newBottom}
    },
    [panelWidth, panelHeight],
  )

  const onDragEnd = useCallback(() => {
    window.removeEventListener('mousemove', onDragMove)
    // eslint-disable-next-line react-hooks/immutability
    window.removeEventListener('mouseup', onDragEnd)
    setIsDragging(false)
    dragStartRef.current = null

    copilotLocalStorage.setPanelPositionRight(newPositionRef.current.right)
    copilotLocalStorage.setPanelPositionBottom(newPositionRef.current.bottom)
  }, [onDragMove])

  const startDrag = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return
      e.preventDefault()

      dragStartRef.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        startRight: positionRight,
        startBottom: positionBottom,
      }
      setIsDragging(true)

      window.addEventListener('mousemove', onDragMove)
      window.addEventListener('mouseup', onDragEnd)
    },
    [positionRight, positionBottom, onDragMove, onDragEnd],
  )

  const onDragHandleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const step = 4
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'ArrowRight') {
        const newRight = Math.max(0, positionRight - step)
        setPositionRight(newRight)
        copilotLocalStorage.setPanelPositionRight(newRight)
        e.preventDefault()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'ArrowLeft') {
        const newRight = Math.min(positionRight + step, document.documentElement.clientWidth - panelWidth)
        setPositionRight(newRight)
        copilotLocalStorage.setPanelPositionRight(newRight)
        e.preventDefault()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'ArrowDown') {
        const newBottom = Math.max(0, positionBottom - step)
        setPositionBottom(newBottom)
        copilotLocalStorage.setPanelPositionBottom(newBottom)
        e.preventDefault()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'ArrowUp') {
        const newBottom = Math.min(positionBottom + step, document.documentElement.clientHeight - panelHeight)
        setPositionBottom(newBottom)
        copilotLocalStorage.setPanelPositionBottom(newBottom)
        e.preventDefault()
      }
    },
    [positionRight, positionBottom, panelWidth, panelHeight],
  )

  // Clamp position so the panel stays fully on-screen.
  // A ResizeObserver on the document element fires whenever the viewport
  // dimensions change (browser resize, DevTools open, etc.), keeping the
  // panel in-bounds without a noisy window 'resize' listener.
  useEffect(() => {
    const clamp = () => {
      const maxRight = Math.max(0, document.documentElement.clientWidth - panelWidth)
      const maxBottom = Math.max(0, document.documentElement.clientHeight - panelHeight)

      setPositionRight(prev => {
        const clamped = Math.min(prev, maxRight)
        if (clamped !== prev) copilotLocalStorage.setPanelPositionRight(clamped)
        return clamped
      })
      setPositionBottom(prev => {
        const clamped = Math.min(prev, maxBottom)
        if (clamped !== prev) copilotLocalStorage.setPanelPositionBottom(clamped)
        return clamped
      })
    }

    clamp()

    const observer = new ResizeObserver(clamp)
    observer.observe(document.documentElement)
    return () => observer.disconnect()
  }, [panelWidth, panelHeight])

  // Keep ref in sync when position changes externally (e.g., from resize)
  useEffect(() => {
    newPositionRef.current = {right: positionRight, bottom: positionBottom}
  }, [positionRight, positionBottom])

  return {
    positionRight,
    positionBottom,
    isDragging,
    startDrag,
    onDragHandleKeyDown,
    setPositionRight,
    setPositionBottom,
  }
}

async function fetchIndexingStatus(nwo: string): Promise<IndexingState> {
  const res = await fetch(`/search/check_indexing_status?nwo=${encodeURIComponent(nwo)}`, {
    headers: {Accept: 'application/json', ...getBaseFetchHeaders()},
  })

  if (!res.ok) {
    return {
      requestStatus: CanIndexStatus.RequestFailed,
      code: TopicIndexStatus.Unknown,
      docs: TopicIndexStatus.Unknown,
    }
  }

  const data = (await res.json()) as IndexingStateResponse

  return {
    requestStatus: data.can_index,
    code: data.code_status,
    docs: data.docs_status,
  }
}

async function triggerIndexing(nwos: string[]) {
  const nwosParam =
    nwos.length === 1 ? `nwo=${encodeURIComponent(nwos[0]!)}` : `nwos=${encodeURIComponent(JSON.stringify(nwos))}`

  return verifiedFetch(`/search/index_embeddings?${nwosParam}&index_code=${true}`, {method: 'POST'})
}

function aggregateResults(repoStates: IndexingState[]): IndexingState {
  return {
    requestStatus: aggregateCanIndexStatuses(repoStates.map(s => s.requestStatus)),
    code: aggregateTopicIndexStatuses(repoStates.map(s => s.code)),
    docs: aggregateTopicIndexStatuses(repoStates.map(s => s.docs)),
    remainingRepoIndexTokens: repoStates[0]?.remainingRepoIndexTokens,
  }
}

function aggregateTopicIndexStatuses(statuses: TopicIndexStatus[]): TopicIndexStatus {
  // if every repo is "indexed", return "indexed"
  if (statuses.every(s => s === TopicIndexStatus.Indexed)) return TopicIndexStatus.Indexed
  // If every repo is "unindexed", return "unindexed"
  if (statuses.every(s => s === TopicIndexStatus.Unindexed)) return TopicIndexStatus.Unindexed
  // If any repo is "indexing", return "indexing"
  if (statuses.some(s => s === TopicIndexStatus.Indexing)) return TopicIndexStatus.Indexing
  // If any repo is "unindexed", we don't have any "indexing" repos, and not all repos are indexed/unindexed, return "partially indexed"
  if (statuses.some(s => s === TopicIndexStatus.Unindexed)) return TopicIndexStatus.PartiallyIndexed
  // Fallback to "unknown"
  return TopicIndexStatus.Unknown
}

function aggregateCanIndexStatuses(statuses: CanIndexStatus[]): CanIndexStatus {
  // If we don't have any statuses, return unknown
  if (statuses.length === 0) return CanIndexStatus.Unknown
  // If any status is error, return "indexing error"
  if (
    statuses.some(s =>
      errorStatuses.includes(
        // @ts-expect-error some options for status might not be in errorStatuses
        s,
      ),
    )
  )
    return CanIndexStatus.IndexingError
  // If all statuses are "can index", return "can index"
  if (statuses.every(s => s === CanIndexStatus.CanIndex)) return CanIndexStatus.CanIndex
  // If all statuses are "unknown", return "unknown"
  if (statuses.every(s => s === CanIndexStatus.Unknown)) return CanIndexStatus.Unknown
  // Fallback to "unknown"
  return CanIndexStatus.Unknown
}

function useInterval(callback: () => void, delay: number) {
  const intervalRef = useRef<number | undefined>(undefined)
  const savedCallback = useEffectEvent(callback)
  useEffect(() => {
    const tick = () => savedCallback()
    if (delay > 0) {
      intervalRef.current = window.setInterval(tick, delay)
      return () => window.clearInterval(intervalRef.current)
    }
  }, [delay])
  return intervalRef
}
